import { List, Map, Record } from "immutable";
import { ProductOptionRecord, productOptionFromJSON } from "common/records/ProductOption";
import { RootActions, RootState } from "common/reducers";
import {
  RECEIVE_PRODUCT_OPTION,
  RECEIVE_PRODUCT_OPTIONS,
  RESET_PRODUCT_OPTION,
  PUSH_IMAGE_ID,
  RESET_IMAGE_IDS,
  UPDATE_PRODUCT_OPTION,
} from "common/reducers/productOption.action";
import { ProductOptionActions } from "common/reducers/productOption.action";
import { receive, reset } from "common/reducers/Utils";
import { ImageActions } from "common/reducers/image.actions";
import { reducer as imageReducer } from "common/reducers/image.reducer";
import { reducer as skuReducer } from "common/reducers/sku.reducer";
import { SkuActions } from "common/reducers/sku.actions";
import { image } from "common/selectors/image.selectors";
import { reducer as rsfFormReducer } from "common/reducers/rsfForm.reducer";
import { RsfFormActions } from "common/reducers/rsfForm.actions";

export const ProductOptionStateRecord = Record({
  byId: Map<number, ProductOptionRecord>(),
  lastSavedById: Map<number, ProductOptionRecord>(),
});

export const initialState = ProductOptionStateRecord();

export type ProductOptionState = typeof initialState;

export type ProductOptionReducerState = RootState;

const model = "productOptions";
export const reducer = (state: RootState, action: RootActions): RootState => {
  if (state && !state.getIn([model])) {
    state = state.setIn([model], initialState);
  }

  switch (action.type) {
    case RECEIVE_PRODUCT_OPTIONS:
      const productOptions = List(action.payload.productOptions);
      productOptions.forEach((productOption) => {
        state = reducer(state, ProductOptionActions.receiveProductOption(productOption));
      });
      return state;

    case RECEIVE_PRODUCT_OPTION:
      const productOption: ProductOptionRecord = productOptionFromJSON(action.payload.productOption);
      if (action.payload.productOption.images?.length) {
        action.payload.productOption.images.forEach((image) => {
          state = imageReducer(state, ImageActions.receiveImage(image));
        });
      }

      if (action.payload.productOption.sku) {
        state = skuReducer(state, SkuActions.receiveSku(action.payload.productOption.sku));
      }
      state = receive(state, model, productOption) as RootState;
      return state;

    case RESET_PRODUCT_OPTION:
      return reset(state, model, action.payload.productOptionId) as RootState;

    case PUSH_IMAGE_ID: {
      const { imageId } = action.payload;
      const imageRecord = image(state, { imageId });
      if (!imageRecord) {
        return state;
      }
      const productOptionId = imageRecord.imageable_id;

      return state.updateIn([model, "byId", productOptionId, "image_ids"], (ids: List<number>) => {
        if (ids?.indexOf(imageId) < 0) return (ids || List()).push(imageId);
        return ids;
      });
    }

    case RESET_IMAGE_IDS: {
      const { productOptionId } = action.payload;
      return state.setIn([model, "byId", productOptionId, "image_ids"], List());
    }

    case UPDATE_PRODUCT_OPTION: {
      const { event } = action.payload;
      state = rsfFormReducer(state, RsfFormActions.update(event));
      if (event.name === "product_price" || event.name === "labor_price") {
        const laborPrice = state.getIn(event.rootPath.concat("labor_price"));
        const productPrice = state.getIn(event.rootPath.concat("product_price"));

        state = state.setIn(event.rootPath.concat("price"), productPrice + laborPrice);
      }
      return state;
    }

    default:
      return state;
  }
};
